import React, { useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';

import { SubscriptionTypes } from '../../types/subscription.types';
import { CustomerService } from '../../services/customer.service';
import { RootState } from '../../store';
import { countLicensesConsumed } from '../../helpers/licenseUsage';

interface Props {
    subscription: SubscriptionTypes.Model;
    visible: boolean;
    onClose: () => void;
    /** Called after a successful save so the owner can refetch its own data. */
    onSaved?: (licenseCount: number) => void;
}

export const LICENSE_COUNT_MAX = 1_000_000;

const validationSchema = Yup.object().shape({
    licenseCount: Yup.number()
        .typeError('Enter a whole number')
        .integer('Enter a whole number')
        .min(0, 'License count cannot be negative')
        .max(LICENSE_COUNT_MAX, 'License count is too large')
        .required('Enter the number of licenses'),
});

/**
 * POM Master dialog for editing a subscription's mobile-app license cap
 * (ClickUp 868m4vzj7). Lowering below current usage is allowed: the cap is
 * an over-usage flag for CS rather than a hard block, so the dialog warns
 * instead of refusing.
 */
export const EditLicenseCountModal: React.FC<Props> = ({
    subscription,
    visible,
    onClose,
    onSaved,
}) => {
    const { loading } = useSelector((state: RootState) => state.customer);

    const consumed = countLicensesConsumed(subscription);
    const current = Number(subscription?.licenseCount ?? 0);

    const formik = useFormik<{ licenseCount: string }>({
        initialValues: { licenseCount: String(current) },
        validationSchema,
        onSubmit: async (values) => {
            const next = Number(values.licenseCount);
            const result = await CustomerService.changeLicenseCount(subscription.id, next);

            if (result) {
                onSaved?.(result.licenseCount);
                onClose();
            }
        },
    });

    const { handleSubmit, errors, touched, getFieldProps, values, resetForm } = formik;

    // Start from the stored value each time the dialog opens.
    useEffect(() => {
        if (visible) {
            resetForm({ values: { licenseCount: String(current) } });
        }
    }, [visible, current, resetForm]);

    const nextValue = values.licenseCount === '' ? NaN : Number(values.licenseCount);
    const belowUsage = Number.isFinite(nextValue) && nextValue < consumed;
    const unchanged = Number.isFinite(nextValue) && nextValue === current;
    const saving = Boolean(loading?.changeLicenseCount);

    return (
        <Dialog open={visible} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ ml: 3, mb: 2, mt: 2 }} fontSize={18} fontWeight={400} color="text.default">
                        Edit License Count
                    </Typography>
                    <Tooltip title="Close">
                        <IconButton
                            onClick={onClose}
                            disabled={saving}
                            aria-label="Close"
                            component="span"
                            sx={{ mr: 2, height: 30, width: 30, alignSelf: 'center' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <FormikProvider value={formik}>
                <Form noValidate onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {consumed} of {current} licenses are currently in use
                                {subscription?.customerName ? ` by ${subscription.customerName}` : ''}.
                            </Typography>
                            <TextField
                                label="Subscription License Count"
                                type="number"
                                fullWidth
                                autoFocus
                                inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                                {...getFieldProps('licenseCount')}
                                error={Boolean(touched.licenseCount && errors.licenseCount)}
                                helperText={
                                    (touched.licenseCount && errors.licenseCount) ||
                                    'Number of mobile app licenses for this organization.'
                                }
                            />
                            {belowUsage && (
                                <Alert severity="warning">
                                    This is below the {consumed} licenses currently in use. Existing users
                                    keep access, and the subscription will show as over its license limit.
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} color="inherit" disabled={saving}>
                            Cancel
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={saving}
                            disabled={unchanged || values.licenseCount === ''}
                        >
                            Save
                        </LoadingButton>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    );
};
